<template>
  <div>
    <ClaimInvoicesOverview class="mb-5" :amount-due="amountDue" />
    <DateRangePicker
      class="mt-2"
      v-model="date"
      placeholder="Veuillez sélectionner la période de résolution de sinistre"
    />
    <SearchBar
      class="my-4"
      :placeholder="$t('Rechercher une facture de sinistre')"
      :initial-keywords="keywords"
      @updateKeywordSearch="updateKeywords"
    />
    <Table
      v-if="claimInvoices.length > 0"
      :columns="columns"
      :data="claimInvoices"
      :isDownloadable="true"
      :isAccessible="true"
      @download="download"
      @view="rowClick"
      @sort="sortBy"
    >
      <template #cost="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div class="text-sm/6 font-medium text-gray-900">{{ $n(row.invoiceAmount, "currency") }}</div>
            </div>
          </div>
        </div>
      </template>
      <template #paid="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div
                :class="[
                  row.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
                  'rounded-md px-2 text-xs font-medium',
                ]"
              >
                {{ row.paid ? $t("paid") : $t("Pending payment") }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import { DateRangePicker, Pagination, Table } from "@estaly/ui";
import SearchBar from "@/components/menu/SearchBar.vue";
import { isPresent } from "@/utils/validation";
import ClaimService from "../../../api/services/claims";
import { toCamelCase } from "@/utils/utils";
import ClaimInvoicesOverview from "@/components/billing/claim_invoice/ClaimInvoicesOverview.vue";

export default {
  props: {
    claimDetailsRoutePrefix: {
      type: String,
      required: true,
    },
    storeId: {
      type: Number,
      required: false,
    },
  },

  components: {
    ClaimInvoicesOverview,
    DateRangePicker,
    SearchBar,
    Pagination,
    Table,
  },

  data() {
    return {
      claimInvoices: [],
      amountDue: null,
      keywords: null,
      startDate: null,
      endDate: null,
      currentPage: 1,
      totalCount: null,
      totalPages: null,
      sortColumn: null,
      sortOrder: null,
      order: null,
      columns: [
        { label: "Sinistres", field: "friendlyId" },
        { label: "Date de résolution", field: "resolutionDate", sortable: true },
        { label: "Type", field: "handlingType" },
        { label: "Magasin", field: "store" },
        { label: "Montant facture", slot: "cost", field: "invoiceAmount" },
        { label: "Statut", slot: "paid", field: "paid" },
      ],
    };
  },

  watch: {
    date() {
      this.handleChangePage(1);
    },
  },

  computed: {
    params() {
      return {
        page: this.currentPage,
        keywords: this.keywords,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        storeId: this.storeId,
      };
    },

    date: {
      get() {
        return [this.startDate, this.endDate];
      },
      set(value) {
        if (value && value.length === 2) {
          this.startDate = value[0];
          this.endDate = value[1];
        } else {
          this.startDate = null;
          this.endDate = null;
        }
      },
    },
  },

  mounted() {
    this.retrieveClaimInvoices();
  },

  methods: {
    async retrieveClaimInvoices() {
      if (this.sortColumn) {
        const direction = this.sortOrder === "ASC" ? "" : "-";
        const columnEquivalent = this.sortColumn === "resolutionDate" ? "claim.resolution_date" : this.sortColumn;
        this.order = `${direction}${columnEquivalent}`;
      }

      const response = await ClaimService.getClaimInvoices(this.params);
      if (!response?.success) return;

      const formattedResponse = toCamelCase(response.data);
      this.claimInvoices = formattedResponse.claimInvoices;
      this.amountDue = formattedResponse.amountDue;
      this.totalCount = formattedResponse.page.totalCount;
      this.totalPages = formattedResponse.page.totalPages;
    },

    handleChangePage(newPage) {
      this.currentPage = newPage;
      this.retrieveClaimInvoices();
    },

    updateKeywords(keywords) {
      this.keywords = keywords;
      this.handleChangePage(1);
    },

    download(row) {
      window.open(row.file, "_blank");
    },

    rowClick(row) {
      window.open(`${this.claimDetailsRoutePrefix}/${row.friendlyId}`, "_blank");
    },

    sortBy(column) {
      if (!isPresent(this.claimInvoices)) return;
      this.sortColumn = column.field;
      this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
      this.handleChangePage(1);
    },
  },
};
</script>
